import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function AdminManagers() {
  const [managers, setManagers] = useState([]);
  const [stores, setStores] = useState([]);
  const [form, setForm] = useState({ name: "", username: "", password: "", store_id: "" });

  const load = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([api.get("/managers"), api.get("/stores")]);
      setManagers(m.data);
      setStores(s.data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const storeName = (id) => stores.find((s) => s.id === id)?.name || "—";

  const add = async (e) => {
    e.preventDefault();
    if (!form.store_id) { toast.error("Pick a store"); return; }
    try {
      await api.post("/managers", { ...form, username: form.username.trim().toLowerCase() });
      setForm({ name: "", username: "", password: "", store_id: "" });
      toast.success("Manager created");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this manager? Their past entries are kept.")) return;
    try {
      await api.delete(`/managers/${id}`);
      toast.success("Removed");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const reassign = async (m, store_id) => {
    try {
      await api.put(`/managers/${m.id}`, { store_id });
      toast.success("Store updated");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const resetPassword = async (m) => {
    const pw = window.prompt(`New password for ${m.username}`);
    if (!pw) return;
    try {
      await api.put(`/managers/${m.id}`, { password: pw });
      toast.success("Password reset");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1200px]">
      <div className="mb-6">
        <div className="label-uc">Access</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Managers</h1>
        <p className="text-sm text-zinc-500 mt-1">Each manager logs in to fill labour for their assigned store.</p>
      </div>

      <form onSubmit={add} className="bg-white border border-zinc-200 p-5 mb-6 grid md:grid-cols-5 gap-3 items-end" data-testid="manager-add-form">
        <div>
          <Label className="label-uc">Name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="rounded-none border-zinc-300 mt-1.5" required data-testid="manager-name-input" />
        </div>
        <div>
          <Label className="label-uc">Username</Label>
          <Input type="text" autoCapitalize="none" spellCheck="false" value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            placeholder="e.g. joe.camden"
            className="rounded-none border-zinc-300 mt-1.5 font-mono-data" required data-testid="manager-username-input" />
        </div>
        <div>
          <Label className="label-uc">Password</Label>
          <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="rounded-none border-zinc-300 mt-1.5 font-mono-data" required data-testid="manager-password-input" />
        </div>
        <div>
          <Label className="label-uc">Store</Label>
          <Select value={form.store_id} onValueChange={(v) => setForm({ ...form, store_id: v })}>
            <SelectTrigger className="rounded-none border-zinc-300 mt-1.5" data-testid="manager-store-select">
              <SelectValue placeholder="Select store" />
            </SelectTrigger>
            <SelectContent>
              {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" className="rounded-none bg-zinc-950 hover:bg-zinc-800 h-10" data-testid="manager-add-button">
          <Plus className="w-4 h-4 mr-2" /> Create
        </Button>
      </form>

      <div className="bg-white border border-zinc-200">
        <table className="w-full text-sm" data-testid="managers-table">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="px-4 py-2 label-uc">Name</th>
              <th className="px-4 py-2 label-uc">Username</th>
              <th className="px-4 py-2 label-uc">Store</th>
              <th className="px-4 py-2 w-40"></th>
            </tr>
          </thead>
          <tbody>
            {managers.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-12 text-center text-zinc-400">No managers yet.</td></tr>
            )}
            {managers.map((m) => (
              <tr key={m.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`manager-row-${m.id}`}>
                <td className="px-4 py-2">{m.name}</td>
                <td className="px-4 py-2 font-mono-data text-xs">{m.email}</td>
                <td className="px-4 py-2">
                  <Select value={m.store_id || ""} onValueChange={(v) => reassign(m, v)}>
                    <SelectTrigger className="rounded-none border-zinc-300 h-8 w-56" data-testid={`manager-store-${m.id}`}>
                      <SelectValue placeholder={storeName(m.store_id)} />
                    </SelectTrigger>
                    <SelectContent>
                      {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => resetPassword(m)} className="text-zinc-500 hover:text-zinc-900" title="Reset password" data-testid={`manager-pw-${m.id}`}>
                      <KeyRound className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(m.id)} className="text-zinc-400 hover:text-[#DF1A22]" data-testid={`manager-delete-${m.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
